// Sprint 4A.7 — Riga audit raggruppata ("Mario ha modificato 3 campi") espandibile.
// Sprint 4A.7.1: gestione array_diff (added/removed/modified) e array_scalar_diff.
import React, { useState } from "react";
import { ChevronDown, ChevronRight, Edit3, AlertTriangle, Plus, Minus, Pencil } from "lucide-react";
import { getFieldLabel, formatAuditValue } from "@/lib/audit_field_labels";

function fmtTs(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ts; }
}

// Rende un item di array_diff (added/removed/modified) come piccola sub-tabella
function ArrayDiffCell({ field, diff }) {
  const fieldKey = field; // path completo, es. "importi.spese_generiche"
  const itemLabel = (item) => {
    if (!item) return "—";
    return item.descrizione || item.label || item.nome || `#${item.id || "?"}`;
  };
  const fmtMoney = (v) => {
    if (typeof v !== "number") return formatAuditValue(v, "importo");
    try {
      return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v);
    } catch { return String(v); }
  };
  return (
    <div className="space-y-1 text-xs" data-testid={`array-diff-${fieldKey}`}>
      {(diff.added || []).map((it, i) => (
        <div key={`a-${i}`} className="flex items-center gap-1.5 text-emerald-700" data-testid={`array-diff-added-${i}`}>
          <Plus className="h-3 w-3" />
          <span className="font-medium">{itemLabel(it)}</span>
          {typeof it.importo !== "undefined" && (
            <span className="text-slate-600">— {fmtMoney(it.importo)}</span>
          )}
          <span className="text-slate-400">(aggiunto)</span>
        </div>
      ))}
      {(diff.removed || []).map((it, i) => (
        <div key={`r-${i}`} className="flex items-center gap-1.5 text-rose-700" data-testid={`array-diff-removed-${i}`}>
          <Minus className="h-3 w-3" />
          <span className="font-medium line-through">{itemLabel(it)}</span>
          {typeof it.importo !== "undefined" && (
            <span className="text-slate-600">— {fmtMoney(it.importo)}</span>
          )}
          <span className="text-slate-400">(rimosso)</span>
        </div>
      ))}
      {(diff.modified || []).map((m, i) => (
        <div key={`m-${i}`} className="flex items-start gap-1.5 text-blue-700" data-testid={`array-diff-modified-${i}`}>
          <Pencil className="h-3 w-3 mt-0.5" />
          <div>
            <span className="font-medium">{m.label || itemLabel(m)}</span>
            <span className="text-slate-400"> (modificato)</span>
            <ul className="ml-3 mt-0.5 text-slate-700">
              {Object.entries(m.changes || {}).map(([k, v]) => (
                <li key={k}>
                  <span className="text-slate-500">{getFieldLabel(k)}:</span>{" "}
                  <span className="text-slate-500 line-through decoration-rose-300/70">
                    {formatAuditValue(v.from, k)}
                  </span>
                  <span className="text-slate-400 mx-1">→</span>
                  <span className="text-slate-900">{formatAuditValue(v.to, k)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
}

function ArrayScalarDiffCell({ diff }) {
  return (
    <div className="space-y-0.5 text-xs">
      {(diff.added || []).map((v, i) => (
        <div key={`a-${i}`} className="flex items-center gap-1.5 text-emerald-700">
          <Plus className="h-3 w-3" /> <span>{String(v)}</span>
        </div>
      ))}
      {(diff.removed || []).map((v, i) => (
        <div key={`r-${i}`} className="flex items-center gap-1.5 text-rose-700">
          <Minus className="h-3 w-3" /> <span className="line-through">{String(v)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Riga audit raggruppata. Props:
 *  - entry: oggetto audit con action terminante in ".updated" e meta.changes
 *  - idx (per data-testid)
 */
export function AuditGroupedRow({ entry, idx }) {
  const [expanded, setExpanded] = useState(false);
  const meta = entry.meta || {};
  const changes = meta.changes || {};
  const n = meta.n_fields_changed ?? Object.keys(changes).length;
  const user = entry.user_email || entry.user_id || "—";
  const ts = fmtTs(entry.timestamp);
  return (
    <li className="border-l-2 border-slate-200 pl-3 py-2" data-testid={`audit-grouped-${idx}`}>
      <button
        type="button"
        onClick={() => setExpanded((s) => !s)}
        className="flex items-center gap-2 w-full text-left hover:text-slate-900"
        data-testid={`audit-grouped-toggle-${idx}`}
      >
        {expanded
          ? <ChevronDown className="h-4 w-4 text-slate-400" />
          : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <Edit3 className="h-3.5 w-3.5 text-blue-600" />
        <span className="text-sm text-slate-800">
          <span className="font-medium">{user}</span>
          <span className="text-slate-500"> ha modificato </span>
          <span className="font-medium text-blue-700">{n} camp{n === 1 ? "o" : "i"}</span>
        </span>
        <span className="text-xs text-slate-400 ml-auto">{ts}</span>
      </button>
      {expanded && (
        <div className="mt-2 ml-6 overflow-x-auto">
          <table className="w-full text-xs border border-slate-200 rounded" data-testid={`audit-grouped-table-${idx}`}>
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-2 py-1 font-medium border-b border-slate-200">Campo</th>
                <th className="text-left px-2 py-1 font-medium border-b border-slate-200">Da</th>
                <th className="text-left px-2 py-1 font-medium border-b border-slate-200">A</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(changes).map(([k, v]) => {
                // Sprint 4A.7.1: array_diff e array_scalar_diff
                if (v && v._type === "array_diff") {
                  return (
                    <tr key={k} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-1 text-slate-700 align-top">{getFieldLabel(k)}</td>
                      <td className="px-2 py-1 align-top" colSpan={2}>
                        <ArrayDiffCell field={k} diff={v} />
                      </td>
                    </tr>
                  );
                }
                if (v && v._type === "array_scalar_diff") {
                  return (
                    <tr key={k} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-1 text-slate-700 align-top">{getFieldLabel(k)}</td>
                      <td className="px-2 py-1 align-top" colSpan={2}>
                        <ArrayScalarDiffCell diff={v} />
                      </td>
                    </tr>
                  );
                }
                // Default: from / to scalari
                return (
                  <tr key={k} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-2 py-1 text-slate-700">{getFieldLabel(k)}</td>
                    <td className="px-2 py-1 text-slate-500 line-through decoration-rose-300/70">
                      {formatAuditValue(v.from, k)}
                    </td>
                    <td className="px-2 py-1 text-slate-900">{formatAuditValue(v.to, k)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {meta.note && (
            <div className="mt-2 text-xs text-slate-600 italic">Nota: "{meta.note}"</div>
          )}
        </div>
      )}
    </li>
  );
}

/** Riga audit per eventi "forced_sotto_margine" (vendita). */
export function AuditForcedSottoMargineRow({ entry, idx }) {
  const meta = entry.meta || {};
  const user = entry.user_email || entry.user_id || "—";
  const motivo = meta.motivo || meta.reason || "";
  return (
    <li className="border-l-2 border-amber-300 pl-3 py-2 bg-amber-50/40" data-testid={`audit-forced-${idx}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <span className="text-sm text-slate-800 font-medium">Vendita confermata sotto margine</span>
        <span className="text-xs text-slate-500">— {user}</span>
      </div>
      {motivo && <div className="text-xs text-slate-600 italic mt-1">"{motivo}"</div>}
      <div className="text-xs text-slate-500 mt-1">{fmtTs(entry.timestamp)}</div>
    </li>
  );
}

/** Riga audit "legacy" (formato vecchio: action.update, no grouped changes). */
export function AuditLegacyRow({ entry, idx }) {
  const user = entry.user_email || entry.user_id || "—";
  return (
    <li className="border-l-2 border-slate-200 pl-3 py-1" data-testid={`audit-legacy-${idx}`}>
      <div className="text-slate-700 text-sm">
        <span className="font-medium">{entry.action}</span>
        <span className="text-slate-500"> — {user}</span>
      </div>
      <div className="text-xs text-slate-500">{fmtTs(entry.timestamp)}</div>
    </li>
  );
}

/**
 * Seleziona il rendering giusto in base all'action.
 * NB: per transizioni di status del veicolo il rendering speciale resta
 * nel chiamante (VehicleDetail) perché usa DisponibilitaBadge.
 */
export function AuditRowAuto({ entry, idx }) {
  const action = entry.action || "";
  if (action.endsWith(".updated")) {
    return <AuditGroupedRow entry={entry} idx={idx} />;
  }
  if (action === "vendita.forced_sotto_margine" || action === "vehicle.forced_margin_bypass") {
    return <AuditForcedSottoMargineRow entry={entry} idx={idx} />;
  }
  return <AuditLegacyRow entry={entry} idx={idx} />;
}
